/**
 * Dynamic color scheme utility for goal progress
 * Provides consistent Cool-to-Hot-to-Gold color progression
 */

export interface ProgressColors {
  progressGradient: string;
  cardColor: string;
  cardBg: string;
  cardBorder: string;
  cardGradient: string;
  shadowClass: string;
  hoverColor: string;
}

/**
 * Get dynamic color scheme based on progress percentage
 * @param progress - Progress percentage (0-100)
 * @returns ProgressColors object with gradient and card styling
 */
export const getProgressColors = (progress: number): ProgressColors => {
  if (progress === 100) {
    return {
      progressGradient: "from-yellow-400 to-amber-500", // Gold
      cardColor: "text-yellow-600 dark:text-yellow-400",
      cardBg: "bg-yellow-50 dark:bg-yellow-900/20",
      cardBorder: "border-yellow-200 dark:border-yellow-800",
      cardGradient: "from-yellow-400 to-amber-500",
      shadowClass: "shadow-yellow-500/25",
      hoverColor: "group-hover:text-yellow-500 dark:group-hover:text-yellow-300"
    };
  } else if (progress >= 80) {
    return {
      progressGradient: "from-orange-500 to-red-500", // Hot
      cardColor: "text-orange-600 dark:text-orange-400",
      cardBg: "bg-orange-50 dark:bg-orange-900/20",
      cardBorder: "border-orange-200 dark:border-orange-800",
      cardGradient: "from-orange-500 to-red-500",
      shadowClass: "shadow-orange-500/25",
      hoverColor: "group-hover:text-orange-500 dark:group-hover:text-orange-300"
    };
  } else if (progress >= 60) {
    return {
      progressGradient: "from-yellow-500 to-orange-500", // Warm
      cardColor: "text-yellow-600 dark:text-yellow-400",
      cardBg: "bg-yellow-50 dark:bg-yellow-900/20",
      cardBorder: "border-yellow-200 dark:border-yellow-800",
      cardGradient: "from-yellow-500 to-orange-500",
      shadowClass: "shadow-yellow-500/25",
      hoverColor: "group-hover:text-yellow-500 dark:group-hover:text-yellow-300"
    };
  } else if (progress >= 40) {
    return {
      progressGradient: "from-green-500 to-emerald-500", // Moderate
      cardColor: "text-green-600 dark:text-green-400",
      cardBg: "bg-green-50 dark:bg-green-900/20",
      cardBorder: "border-green-200 dark:border-green-800",
      cardGradient: "from-green-500 to-emerald-500",
      shadowClass: "shadow-green-500/25",
      hoverColor: "group-hover:text-green-500 dark:group-hover:text-green-300"
    };
  } else if (progress >= 20) {
    return {
      progressGradient: "from-blue-500 to-cyan-500", // Cool
      cardColor: "text-blue-600 dark:text-blue-400",
      cardBg: "bg-blue-50 dark:bg-blue-900/20",
      cardBorder: "border-blue-200 dark:border-blue-800",
      cardGradient: "from-blue-500 to-cyan-500",
      shadowClass: "shadow-blue-500/25",
      hoverColor: "group-hover:text-blue-500 dark:group-hover:text-blue-300"
    };
  } else {
    return {
      progressGradient: "from-slate-400 to-slate-500", // Neutral
      cardColor: "text-slate-600 dark:text-slate-400",
      cardBg: "bg-slate-50 dark:bg-slate-900/20",
      cardBorder: "border-slate-200 dark:border-slate-800",
      cardGradient: "from-slate-400 to-slate-500",
      shadowClass: "shadow-slate-500/25",
      hoverColor: "group-hover:text-slate-500 dark:group-hover:text-slate-300"
    };
  }
}; 